import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { restaurantInfo } from '../../data/mock';
import { handleImageError } from '../../lib/imageFallback';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Menu / Order', path: '/order' },
    { name: 'Book a Table', path: '/booking' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#FFFBF5]/95 backdrop-blur-sm shadow-md py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <picture>
                <source type="image/webp" srcSet="/images/logo.webp" />
                <img 
                  src="/images/logo.png" 
                  alt="La Pasteria Pizza Bar" 
                  loading="eager"
                  decoding="async"
                  onError={handleImageError}
                  className="h-12 md:h-14 w-auto"
                />
              </picture>
            </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-base font-medium transition-all duration-200 hover:text-[#C67C4E] ${
                  isActive(link.path)
                    ? 'text-[#1B4332] border-b-2 border-[#C67C4E] pb-1'
                    : 'text-[#2D3436]/80'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${restaurantInfo.phone}`}
              className="flex items-center gap-2 text-[#2D3436] font-medium hover:text-[#C67C4E] transition-colors"
            >
              <Phone size={18} />
              <span>{restaurantInfo.phone}</span>
            </a>
            <Link to="/booking">
              <Button className="btn-primary">
                Reserve Table
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#1B4332]"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fadeIn bg-[#FFFBF5] rounded-lg p-4">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-lg font-medium py-2 transition-colors ${
                    isActive(link.path)
                      ? 'text-[#1B4332] font-semibold'
                      : 'text-[#2D3436]/80'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href={`tel:${restaurantInfo.phone}`}
                className="flex items-center gap-2 text-[#2D3436] font-medium py-2"
              >
                <Phone size={18} />
                <span>{restaurantInfo.phone}</span>
              </a>
              <Link to="/booking" onClick={() => setIsMenuOpen(false)}>
                <Button className="btn-primary w-full mt-2">
                  Reserve Table
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
